import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp, ShoppingCart, Package, AlertCircle,
  IndianRupee, Users, ArrowUpRight, ArrowDownRight,
  Clock, Zap, Calendar, ShoppingBag, Tag
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, ComposedChart, Line
} from 'recharts';

const fmt    = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(n || 0));
const fmtCur = (n) => {
  const val = Number(n || 0);
  if (val < 0) return '-₹' + fmt(val);
  return '₹' + fmt(val);
};

const COLORS = ['#d4a017', '#1e3a5f', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b'];

/* ── Custom Tooltip ─────────────────────────────────────── */
function ChartTooltip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0c1a2e', border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem',
      boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
    }}>
      <p style={{ color: '#94a3b8', marginBottom: 6, fontSize: '0.78rem', fontWeight: 700 }}>{label}</p>
      {payload.map((p, i) => {
        const val = Number(p.value || 0);
        const formattedVal = currency ? fmtCur(val) : fmt(val);
        const color = p.name === 'Net Profit' ? (val < 0 ? '#ef4444' : val > 0 ? '#10b981' : '#f59e0b') : p.color;
        return (
          <p key={i} style={{ color: color || '#d4a017', fontWeight: 700, margin: '3px 0' }}>
            {p.name}: {formattedVal}
          </p>
        );
      })}
    </div>
  );
}

function ItemStockTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: '#0c1a2e', border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem',
      boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
    }}>
      <p style={{ color: '#fff', fontWeight: 700, margin: 0, fontSize: '0.85rem' }}>{d.itemName}</p>
      {d.categoryName && <p style={{ color: '#94a3b8', fontSize: '0.75rem', margin: '2px 0 6px 0' }}>Category: {d.categoryName}</p>}
      <p style={{ color: '#10b981', fontWeight: 700, margin: '3px 0' }}>
        Stock Qty: {Number(d.stockQty || 0)} pcs
      </p>
      <p style={{ color: '#3b82f6', fontWeight: 700, margin: '3px 0' }}>
        Stock Weight: {Number(d.stockWeight || 0).toFixed(3)} g
      </p>
    </div>
  );
}

function ItemSalesTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: '#0c1a2e', border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem',
      boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
    }}>
      <p style={{ color: '#fff', fontWeight: 700, margin: 0, fontSize: '0.85rem' }}>{d.itemName}</p>
      {d.categoryName && <p style={{ color: '#94a3b8', fontSize: '0.75rem', margin: '2px 0 6px 0' }}>Category: {d.categoryName}</p>}
      <p style={{ color: '#10b981', fontWeight: 700, margin: '3px 0' }}>
        Sales Amount: {fmtCur(d.salesAmount)}
      </p>
      <p style={{ color: '#f59e0b', fontWeight: 700, margin: '3px 0' }}>
        Sold Qty: {Number(d.soldQty || 0)} pcs
      </p>
    </div>
  );
}

/* ── KPI Card ───────────────────────────────────────────── */
function KpiCard({ label, value, sub, icon: Icon, variant, trend, badge }) {
  return (
    <div className={`stat-card ${variant || ''}`}>
      <div className="stat-icon"><Icon size={18} strokeWidth={2} /></div>
      <div className="stat-label">{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        <div className="stat-value">{value}</div>
        {badge && (
          <div style={{
            fontSize: '0.74rem',
            fontWeight: 700,
            padding: '3px 8px',
            borderRadius: 6,
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fca5a5',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}>
            <span style={{ fontSize: '0.68rem', textTransform: 'uppercase', opacity: 0.85 }}>+3%:</span>
            <span>{badge}</span>
          </div>
        )}
      </div>
      {(sub || trend) && (
        <div className="kpi-footer">
          {trend !== undefined && (
            <span className={`kpi-trend ${trend >= 0 ? 'up' : 'down'}`}>
              {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
              {Math.abs(trend)}%
            </span>
          )}
          {sub && <span className="stat-sub">{sub}</span>}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [kpiPeriod, setKpiPeriod]   = useState('TODAY'); // 'TODAY', 'WEEKLY', 'MONTHLY'
  const [trendType, setTrendType]   = useState('MONTHLY');
  const [finData, setFinData]       = useState([]);
  const [itemStock, setItemStock]   = useState([]);
  const [stockMetric, setStockMetric] = useState('QTY');
  const [itemSales, setItemSales]   = useState([]);
  const [salesMetric, setSalesMetric] = useState('QTY');

  useEffect(() => {
    (async () => {
      try {
        const branchID = user?.roleName === 'Administrator' ? undefined : user?.branchID;
        const res = await dashboardAPI.get(branchID);
        setData(res.data.data);
      } catch (err) {
        console.error('[Dashboard] Error:', err?.response?.data || err.message);
      }
      finally { setLoading(false); }
    })();
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const branchID = user?.roleName === 'Administrator' ? undefined : user?.branchID;
        const res = await dashboardAPI.getFinancialPerformance(trendType, branchID);
        setFinData(res.data.data || []);
      } catch (err) {
        console.error('[Dashboard] Performance Chart Error:', err?.response?.data || err.message);
      }
    })();
  }, [trendType, user]);

  useEffect(() => {
    (async () => {
      try {
        const branchID = user?.roleName === 'Administrator' ? undefined : user?.branchID;
        const res = await dashboardAPI.getItemWiseStock(branchID);
        setItemStock(res.data.data || []);
      } catch (err) {
        console.error('[Dashboard] Item Stock Chart Error:', err?.response?.data || err.message);
      }
    })();
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const branchID = user?.roleName === 'Administrator' ? undefined : user?.branchID;
        const res = await dashboardAPI.getItemwiseSales(branchID);
        setItemSales(res.data.data || []);
      } catch (err) {
        console.error('[Dashboard] Item Sales Chart Error:', err?.response?.data || err.message);
      }
    })();
  }, [user]);

  const can = (module) => {
    if (!user) return false;
    if (user.roleName === 'Administrator') return true;
    if (Array.isArray(user.permissions)) return user.permissions.includes(module);
    if (typeof user.permissions === 'string') return user.permissions.split(',').map(p => p.trim()).includes(module);
    return false;
  };

  const summary      = data?.summary      || {};
  const topCustomers = data?.topCustomers || [];
  const barData      = topCustomers.map(c => ({
    name: (c.customerName || 'Customer').length > 12 ? (c.customerName || 'Customer').substring(0, 10) + '…' : (c.customerName || 'Customer'),
    sales: Number(c.totalSales || 0)
  }));

  const getProp = (obj, ...keys) => {
    if (!obj) return 0;
    for (let k of keys) {
      if (obj[k] !== undefined && obj[k] !== null) return Number(obj[k]);
    }
    return 0;
  };

  // KPI Period Values
  const salesVal = kpiPeriod === 'TODAY' ? getProp(summary, 'todaySales', 'todaysales', 'TodaySales')
    : kpiPeriod === 'WEEKLY' ? getProp(summary, 'weeklySales', 'weeklysales', 'WeeklySales')
    : getProp(summary, 'monthlySales', 'monthlysales', 'MonthlySales');
  
  const salesLabel = kpiPeriod === 'TODAY' ? "Today's Sales"
    : kpiPeriod === 'WEEKLY' ? "This Week's Sales"
    : "This Month's Sales";
  
  const salesSub = kpiPeriod === 'TODAY' ? "Revenue collected today"
    : kpiPeriod === 'WEEKLY' ? "Revenue collected this week"
    : "Revenue collected this month";

  const purchVal = kpiPeriod === 'TODAY' ? getProp(summary, 'todayPurchase', 'todaypurchase', 'TodayPurchase')
    : kpiPeriod === 'WEEKLY' ? getProp(summary, 'weeklyPurchase', 'weeklypurchase', 'WeeklyPurchase')
    : getProp(summary, 'monthlyPurchase', 'monthlypurchase', 'MonthlyPurchase');
  
  const purchLabel = kpiPeriod === 'TODAY' ? "Today's Purchase"
    : kpiPeriod === 'WEEKLY' ? "This Week's Purchase"
    : "This Month's Purchase";
  
  const purchSub = kpiPeriod === 'TODAY' ? "Purchased today"
    : kpiPeriod === 'WEEKLY' ? "Purchased this week"
    : "Purchased this month";

  const purch3PctVal = kpiPeriod === 'TODAY' ? getProp(summary, 'todayPurchase3Pct', 'todaypurchase3pct', 'TodayPurchase3Pct')
    : kpiPeriod === 'WEEKLY' ? getProp(summary, 'weeklyPurchase3Pct', 'weeklypurchase3pct', 'WeeklyPurchase3Pct')
    : getProp(summary, 'monthlyPurchase3Pct', 'monthlypurchase3pct', 'MonthlyPurchase3Pct');

  const purch3PctLabel = kpiPeriod === 'TODAY' ? "Today's Purchase (3%)"
    : kpiPeriod === 'WEEKLY' ? "This Week's Purchase (3%)"
    : "This Month's Purchase (3%)";

  const profitVal = kpiPeriod === 'TODAY' ? getProp(summary, 'todayProfit', 'todayprofit', 'TodayProfit')
    : kpiPeriod === 'WEEKLY' ? getProp(summary, 'weeklyProfit', 'weeklyprofit', 'WeeklyProfit')
    : getProp(summary, 'monthlyProfit', 'monthlyprofit', 'MonthlyProfit');
  
  const profitLabel = kpiPeriod === 'TODAY' ? "Today's Profit"
    : kpiPeriod === 'WEEKLY' ? "This Week's Profit"
    : "This Month's Profit";

  const salePriceLabel = kpiPeriod === 'TODAY' ? "Today's Sale Price"
    : kpiPeriod === 'WEEKLY' ? "This Week's Sale Price"
    : "This Month's Sale Price";

  // Greeting
  const h = new Date().getHours();
  const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.fullName?.split(' ')[0] || 'there';

  if (loading) return (
    <div className="page-loader">
      <div className="spinner" />
      <span>Loading dashboard...</span>
    </div>
  );

  return (
    <div>
      {/* ── Welcome Row ── */}
      <div className="dash-welcome mb-6">
        <div>
          <h1 className="page-title">{greeting}, {firstName}! 👋</h1>
          <p className="page-subtitle">Here's your business overview.</p>
        </div>
        <div className="dash-quick-actions">
          {can('Sales') && (
            <a href="/sales" className="btn btn-primary btn-sm">
              <Zap size={14} /> New Sale
            </a>
          )}
          {can('Purchase') && (
            <a href="/purchase" className="btn btn-ghost btn-sm">
              <ShoppingCart size={14} /> New Purchase
            </a>
          )}
        </div>
      </div>

      {/* ── KPI Cards Header & Time Period Filter ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--navy-800, #1e293b)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={16} style={{ color: 'var(--primary)' }} />
          Performance Summary
        </div>
        <div style={{ display: 'flex', background: 'var(--silver-100, #f1f5f9)', padding: 3, borderRadius: 20, border: '1px solid var(--border)' }}>
          <button
            type="button"
            className="btn btn-xs"
            onClick={() => setKpiPeriod('TODAY')}
            style={{
              borderRadius: 16, padding: '4px 14px', fontSize: '0.78rem', fontWeight: 700, border: 'none',
              background: kpiPeriod === 'TODAY' ? '#fff' : 'transparent',
              color: kpiPeriod === 'TODAY' ? 'var(--primary-700, #1d4ed8)' : 'var(--silver-600)',
              boxShadow: kpiPeriod === 'TODAY' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            Today
          </button>
          <button
            type="button"
            className="btn btn-xs"
            onClick={() => setKpiPeriod('WEEKLY')}
            style={{
              borderRadius: 16, padding: '4px 14px', fontSize: '0.78rem', fontWeight: 700, border: 'none',
              background: kpiPeriod === 'WEEKLY' ? '#fff' : 'transparent',
              color: kpiPeriod === 'WEEKLY' ? 'var(--primary-700, #1d4ed8)' : 'var(--silver-600)',
              boxShadow: kpiPeriod === 'WEEKLY' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            Weekly
          </button>
          <button
            type="button"
            className="btn btn-xs"
            onClick={() => setKpiPeriod('MONTHLY')}
            style={{
              borderRadius: 16, padding: '4px 14px', fontSize: '0.78rem', fontWeight: 700, border: 'none',
              background: kpiPeriod === 'MONTHLY' ? '#fff' : 'transparent',
              color: kpiPeriod === 'MONTHLY' ? 'var(--primary-700, #1d4ed8)' : 'var(--silver-600)',
              boxShadow: kpiPeriod === 'MONTHLY' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* ── KPI Cards Grid ── */}
      <div className="stat-grid mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))' }}>
        {can('Sales') && (
          <KpiCard
            label={salesLabel}
            value={fmtCur(salesVal)}
            sub={salesSub}
            icon={TrendingUp}
            variant="amber"
          />
        )}
        {can('Purchase') && (
          <KpiCard
            label={purchLabel}
            value={fmtCur(purchVal)}
            badge={fmtCur(purchVal * 1.03)}
            sub={purchSub}
            icon={ShoppingCart}
            variant="blue"
          />
        )}
        {(can('Sales') || can('Purchase')) && (
          <KpiCard
            label={purch3PctLabel}
            value={fmtCur(purch3PctVal)}
            sub="Cost of sold items (3% GST)"
            icon={ShoppingBag}
            variant="purple"
          />
        )}
        {(can('Sales') || can('Reports')) && (
          <KpiCard
            label={profitLabel}
            value={fmtCur(profitVal)}
            sub="Sales Revenue − Purchase 3% Cost"
            icon={IndianRupee}
            variant={profitVal > 0 ? 'green' : profitVal < 0 ? 'red' : 'amber'}
          />
        )}
        {can('Sales') && (
          <KpiCard
            label={salePriceLabel}
            value={fmtCur(salesVal)}
            sub="Total selling price from sales"
            icon={Tag}
            variant="purple"
          />
        )}
      </div>

      {/* ── Financial Performance Timeline ── */}
      {(can('Sales') || can('Purchase') || can('Reports')) && (
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
                <TrendingUp size={18} style={{ color: '#10b981' }} />
                Financial Performance Timeline ({trendType === 'MONTHLY' ? 'MONTH WISE' : 'WEEK WISE'})
              </h3>
              <p className="page-subtitle" style={{ margin: '2px 0 0 0' }}>
                Sales revenue vs Purchase cost of sold items vs Net profit (Sale Price - Purchase Price)
              </p>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setTrendType(t => t === 'MONTHLY' ? 'WEEKLY' : 'MONTHLY')}
              style={{ borderRadius: 20, padding: '5px 14px', fontSize: '0.78rem', fontWeight: 600, background: 'var(--primary-50, #eff6ff)', color: 'var(--primary-700, #1d4ed8)', border: '1px solid var(--primary-200, #bfdbfe)' }}
            >
              View: {trendType === 'MONTHLY' ? 'Weekly Trend' : 'Monthly Trend'}
            </button>
          </div>
          <div className="card-body">
            {/* Custom Legend */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 16, fontSize: '0.8rem', fontWeight: 600 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                <span style={{ color: '#334155' }}>Sales Amount (₹)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
                <span style={{ color: '#334155' }}>Purch. Cost (Sold Pcs) (₹)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
                <span style={{ color: '#334155' }}>Net Profit (₹)</span>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={290}>
              <ComposedChart data={finData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="purGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="periodLabel" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={v => '₹' + (Math.abs(v) >= 1000 ? (v/1000).toFixed(0)+'k' : v)} />
                <Tooltip content={<ChartTooltip currency />} />
                <Area type="monotone" dataKey="salesRevenue" name="Sales Amount" stroke="#10b981" strokeWidth={2.5} fill="url(#salesGrad)" />
                <Area type="monotone" dataKey="purchasesExpense" name="Purch. Cost (Sold Pcs)" stroke="#3b82f6" strokeWidth={2} fill="url(#purGrad)" />
                <Line type="monotone" dataKey="netProfit" name="Net Profit" stroke="#f59e0b" strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 4, fill: '#f59e0b', stroke: '#fff', strokeWidth: 1.5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Item-Wise Breakdown Charts Row ── */}
      <div className="grid-2 mb-6">
        {/* Left: Item-Wise Stock Breakdown */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
                <Package size={18} style={{ color: '#3b82f6' }} />
                Item-Wise Stock Breakdown (Qty Wise)
              </h3>
              <p className="page-subtitle" style={{ margin: '2px 0 0 0' }}>
                Total stock quantity (pcs) and weight (g) in hand by item
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className={`btn btn-xs ${stockMetric === 'QTY' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setStockMetric('QTY')}
                style={{ borderRadius: 20, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600 }}
              >
                Qty (Pcs)
              </button>
              <button
                type="button"
                className={`btn btn-xs ${stockMetric === 'WT' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setStockMetric('WT')}
                style={{ borderRadius: 20, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600 }}
              >
                Weight (g)
              </button>
            </div>
          </div>
          <div className="card-body">
            {itemStock.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={itemStock.map(it => ({ ...it, shortName: (it.itemName || '').length > 12 ? (it.itemName.substring(0,10) + '...') : it.itemName }))} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="stockQtyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0.8} />
                    </linearGradient>
                    <linearGradient id="stockWtGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                      <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="shortName" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={v => stockMetric === 'WT' ? `${v}g` : `${v}`} />
                  <Tooltip content={<ItemStockTooltip />} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
                  <Bar
                    dataKey={stockMetric === 'QTY' ? 'stockQty' : 'stockWeight'}
                    name={stockMetric === 'QTY' ? 'Stock Qty (pcs)' : 'Stock Weight (g)'}
                    fill={stockMetric === 'QTY' ? 'url(#stockQtyGrad)' : 'url(#stockWtGrad)'}
                    radius={[6, 6, 0, 0]}
                    barSize={32}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--silver-500)' }}>
                <Package size={32} style={{ strokeWidth: 1.5, opacity: 0.5, marginBottom: 8 }} />
                <p style={{ margin: 0, fontWeight: 600 }}>No stock items available</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Item-Wise Sales Breakdown */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
                <TrendingUp size={18} style={{ color: '#10b981' }} />
                Item-Wise Sales Breakdown (Qty Wise)
              </h3>
              <p className="page-subtitle" style={{ margin: '2px 0 0 0' }}>
                Total quantity sold (pcs) and sales revenue (₹) by item
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className={`btn btn-xs ${salesMetric === 'AMT' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setSalesMetric('AMT')}
                style={{ borderRadius: 20, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600 }}
              >
                Value (₹)
              </button>
              <button
                type="button"
                className={`btn btn-xs ${salesMetric === 'QTY' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setSalesMetric('QTY')}
                style={{ borderRadius: 20, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600 }}
              >
                Sold Qty
              </button>
            </div>
          </div>
          <div className="card-body">
            {itemSales.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={itemSales.map(it => ({ ...it, shortName: (it.itemName || '').length > 12 ? (it.itemName.substring(0,10) + '...') : it.itemName }))} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesAmtGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                      <stop offset="100%" stopColor="#6d28d9" stopOpacity={0.8} />
                    </linearGradient>
                    <linearGradient id="salesQtyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={1} />
                      <stop offset="100%" stopColor="#d97706" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="shortName" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={v => salesMetric === 'AMT' ? '₹' + (v >= 1000 ? (v/1000).toFixed(0)+'k' : v) : `${v}`} />
                  <Tooltip content={<ItemSalesTooltip />} cursor={{ fill: 'rgba(139,92,246,0.06)' }} />
                  <Bar
                    dataKey={salesMetric === 'AMT' ? 'salesAmount' : 'soldQty'}
                    name={salesMetric === 'AMT' ? 'Sales Value (₹)' : 'Sold Qty (pcs)'}
                    fill={salesMetric === 'AMT' ? 'url(#salesAmtGrad)' : 'url(#salesQtyGrad)'}
                    radius={[6, 6, 0, 0]}
                    barSize={32}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--silver-500)' }}>
                <TrendingUp size={32} style={{ strokeWidth: 1.5, opacity: 0.5, marginBottom: 8 }} />
                <p style={{ margin: 0, fontWeight: 600 }}>No item sales data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Charts Row ── */}
      {(can('Sales') || can('Customers')) && (
        <div className="grid-2 mb-6">
          {/* Bar Chart */}
          <div className="card">
            <div className="card-header">
              <h3>Top Customers</h3>
              <span className="badge badge-navy">This month</span>
            </div>
            <div className="card-body">
              {barData.length > 0 ? (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={barData} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1e3a5f" stopOpacity={1} />
                        <stop offset="100%" stopColor="#2a5080" stopOpacity={0.85} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#94a3b8' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => '₹' + (v >= 1000 ? (v/1000).toFixed(0)+'k' : v)}
                    />
                    <Tooltip content={<ChartTooltip currency />} cursor={{ fill: 'rgba(30,58,95,0.06)' }} />
                    <Bar dataKey="sales" fill="url(#barGrad)" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-state" style={{ height: 230 }}>
                  <Users size={40} />
                  <h4>No sales data yet</h4>
                  <p>Sales will appear here once invoices are added</p>
                </div>
              )}
            </div>
          </div>

          {/* Pie Chart */}
          <div className="card">
            <div className="card-header">
              <h3>Sales Distribution</h3>
              <span className="badge badge-navy">By customer</span>
            </div>
            <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {topCustomers.length > 0 ? (
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={topCustomers}
                      dataKey="totalSales"
                      nameKey="customerName"
                      cx="50%"
                      cy="45%"
                      outerRadius={82}
                      innerRadius={46}
                      paddingAngle={3}
                    >
                      {topCustomers.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtCur(v)} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: '0.78rem', paddingTop: 4 }}
                      formatter={(val) => (
                        <span style={{ color: '#475569', fontWeight: 500 }}>
                          {val.length > 14 ? val.substring(0, 14) + '…' : val}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-state" style={{ height: 230 }}>
                  <TrendingUp size={40} />
                  <h4>No data yet</h4>
                  <p>Distribution will show after sales are recorded</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Top Customers Table ── */}
      {(can('Sales') || can('Customers')) && topCustomers.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div>
              <h3>Top 5 Customers</h3>
              <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>Highest sales this month</p>
            </div>
            <a href="/customers" className="btn btn-ghost btn-sm">View all</a>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Customer</th>
                  <th style={{ textAlign: 'right' }}>Total Sales</th>
                  <th style={{ textAlign: 'right' }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((c, i) => {
                  const total = topCustomers.reduce((s, x) => s + Number(x.totalSales || 0), 0);
                  const pct   = total ? ((Number(c.totalSales || 0) / total) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={i}>
                      <td>
                        <span className={`badge ${['badge-amber','badge-navy','badge-blue','badge-green','badge-gray'][i] || 'badge-gray'}`}>
                          {i + 1}
                        </span>
                      </td>
                      <td>
                        <div className="flex-gap gap-2">
                          <div className="cust-avatar">
                            {(c.customerName || 'N')[0].toUpperCase()}
                          </div>
                          <span className="font-medium">{c.customerName}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="text-mono font-semibold">{fmtCur(c.totalSales)}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="pct-wrap">
                          <span className="text-muted text-sm">{pct}%</span>
                          <div className="pct-bar-bg">
                            <div className="pct-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        .dash-welcome {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .dash-quick-actions { display: flex; gap: 8px; flex-shrink: 0; }

        /* KPI footer */
        .kpi-footer {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
        }
        .kpi-trend {
          display: inline-flex; align-items: center; gap: 3px;
          font-size: 0.72rem; font-weight: 700;
          padding: 2px 7px; border-radius: 99px;
        }
        .kpi-trend.up   { background: var(--success-light); color: var(--success-dark); }
        .kpi-trend.down { background: var(--danger-light);  color: var(--danger-dark); }

        /* Customer avatar in table */
        .cust-avatar {
          width: 28px; height: 28px;
          border-radius: 50%;
          background: var(--primary-100);
          color: var(--primary-700);
          display: flex; align-items: center; justify-content: center;
          font-size: 0.72rem; font-weight: 700;
          flex-shrink: 0;
        }

        /* Pct bar */
        .pct-wrap { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
        .pct-bar-bg {
          width: 80px; height: 4px;
          background: var(--gray-200);
          border-radius: 99px;
          overflow: hidden;
        }
        .pct-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--accent-500), var(--accent-400));
          border-radius: 99px;
          transition: width 0.6s ease;
        }
      `}</style>
    </div>
  );
}
